import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Shield, Scale, Flame, ArrowLeft, TrendingUp, TrendingDown, Activity,
  ChevronDown, ChevronUp, Briefcase, BarChart3, RefreshCw, Zap
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PROFILE_ICONS = {
  conservative: Shield,
  moderate: Scale,
  aggressive: Flame,
};

const PROFILE_COLORS = {
  conservative: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', badge: 'bg-blue-500/20 text-blue-400' },
  moderate: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', badge: 'bg-amber-500/20 text-amber-400' },
  aggressive: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', badge: 'bg-red-500/20 text-red-400' },
};

const RISK_BARS = { conservative: 1, moderate: 2, aggressive: 3 };

function RiskMeter({ level }) {
  return (
    <div className="flex gap-1 items-center">
      {[1, 2, 3].map(i => (
        <div key={i} className={`h-2 w-6 ${i <= level ? (level === 1 ? 'bg-blue-500' : level === 2 ? 'bg-amber-500' : 'bg-red-500') : 'bg-white/10'}`} />
      ))}
    </div>
  );
}

function AgentCard({ agent, colors }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-[#11141D] border border-white/5 hover:border-white/10 transition-colors" data-testid={`agent-card-${agent.id}`}>
      <div className="flex items-center justify-between p-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-8 h-8 ${colors.bg} flex items-center justify-center shrink-0`}>
            <span className="text-[10px] font-mono font-bold text-white">{agent.id.split('-')[1]}</span>
          </div>
          <div className="min-w-0">
            <div className="text-xs font-bold text-white truncate">{agent.name}</div>
            <div className="text-[10px] text-slate-500 font-mono">{agent.type} | {agent.instruments?.length || 0} instruments</div>
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="text-right hidden sm:block">
            <div className={`text-xs font-mono font-bold ${agent.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {agent.pnl >= 0 ? '+' : ''}${agent.pnl?.toLocaleString()}
            </div>
            <div className={`text-[10px] font-mono ${agent.total_return >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {agent.total_return >= 0 ? '+' : ''}{agent.total_return}%
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div className={`w-1.5 h-1.5 rounded-full ${agent.status === 'RUNNING' ? 'bg-emerald-500 animate-pulse' : 'bg-slate-600'}`} />
            {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-white/5 p-3 space-y-3">
          <p className="text-xs text-slate-400">{agent.description}</p>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { label: 'Capital', value: `$${(agent.capital || 0).toLocaleString()}` },
              { label: 'Win Rate', value: `${agent.win_rate || 0}%` },
              { label: 'Trades', value: agent.total_trades || 0 },
              { label: 'Sharpe', value: agent.sharpe_ratio?.toFixed(2) || '0.00' },
            ].map(s => (
              <div key={s.label} className="bg-[#090B0F] p-2">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">{s.label}</div>
                <div className="text-xs font-mono text-white font-bold">{s.value}</div>
              </div>
            ))}
          </div>

          {/* Instruments */}
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">{t('profiles.assignedInstruments')}</div>
            <div className="flex flex-wrap gap-1">
              {agent.instruments?.map(inst => (
                <span key={inst} className={`text-[10px] font-mono px-2 py-0.5 ${colors.badge}`}>{inst}</span>
              ))}
            </div>
          </div>

          {/* Open Positions */}
          {agent.open_positions && agent.open_positions.length > 0 && (
            <div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">{t('profiles.openPositions')}</div>
              {agent.open_positions.map((pos, i) => (
                <div key={i} className="flex justify-between text-[10px] font-mono py-0.5 border-b border-white/5">
                  <span className="text-white">{pos.symbol} <span className={pos.side === 'BUY' ? 'text-emerald-400' : 'text-red-400'}>{pos.side}</span></span>
                  <span className={`${pos.unrealized_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {pos.unrealized_pnl >= 0 ? '+' : ''}${pos.unrealized_pnl?.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function InvestmentProfiles() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfiles = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/profiles`, { withCredentials: true });
      setProfiles(data.profiles);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, []);

  const fetchDetail = useCallback(async (key) => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/profiles/${key}`, { withCredentials: true });
      setDetailData(data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  useEffect(() => {
    if (selectedProfile) fetchDetail(selectedProfile);
  }, [selectedProfile, fetchDetail]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030508] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  const profileOrder = ['conservative', 'moderate', 'aggressive'];

  return (
    <div className="min-h-screen bg-[#030508] text-white" data-testid="investment-profiles-page">
      {/* Header */}
      <div className="border-b border-white/5 bg-[#030508]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="text-slate-500 hover:text-white transition-colors" data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-emerald-500" />
              <h1 className="text-sm font-black uppercase tracking-[0.15em]">{t('profiles.title')}</h1>
            </div>
          </div>
          <button onClick={fetchProfiles} className="text-slate-500 hover:text-white transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Profile Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {profileOrder.map(key => {
            const p = profiles?.[key];
            if (!p) return null;
            const Icon = PROFILE_ICONS[key];
            const colors = PROFILE_COLORS[key];
            const isActive = selectedProfile === key;

            return (
              <div
                key={key}
                onClick={() => setSelectedProfile(isActive ? null : key)}
                className={`cursor-pointer border p-5 transition-all ${isActive ? `${colors.border} ${colors.bg}` : 'border-white/10 bg-[#090B0F] hover:border-white/20'}`}
                data-testid={`profile-card-${key}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-9 h-9 ${colors.bg} flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${colors.text}`} />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-white">{p.name}</h3>
                      <span className="text-[10px] text-slate-500">{p.name_es}</span>
                    </div>
                  </div>
                  <RiskMeter level={RISK_BARS[key]} />
                </div>

                <p className="text-xs text-slate-400 mb-4 leading-relaxed">{p.description}</p>

                {/* Profile Stats */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-[#030508] p-2 text-center">
                    <div className="text-lg font-black text-white">{p.agent_count}</div>
                    <div className="text-[9px] text-slate-500 uppercase tracking-wider">{t('profiles.agents')}</div>
                  </div>
                  <div className="bg-[#030508] p-2 text-center">
                    <div className="text-lg font-black text-white">{p.instrument_count}</div>
                    <div className="text-[9px] text-slate-500 uppercase tracking-wider">{t('profiles.instruments')}</div>
                  </div>
                  <div className="bg-[#030508] p-2 text-center">
                    <div className={`text-lg font-black ${p.total_return >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {p.total_return >= 0 ? '+' : ''}{p.total_return}%
                    </div>
                    <div className="text-[9px] text-slate-500 uppercase tracking-wider">{t('common.return')}</div>
                  </div>
                </div>

                {/* Key Instruments */}
                <div className="flex flex-wrap gap-1">
                  {p.key_instruments?.slice(0, 8).map(inst => (
                    <span key={inst} className={`text-[10px] font-mono px-1.5 py-0.5 ${colors.badge}`}>{inst}</span>
                  ))}
                  {p.key_instruments?.length > 8 && (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 bg-white/5 text-slate-500">
                      +{p.key_instruments.length - 8}
                    </span>
                  )}
                </div>

                {/* Target Allocation */}
                <div className="mt-3 pt-3 border-t border-white/5">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">{t('profiles.targetAllocation')}</div>
                  <div className="text-[11px] text-slate-400 font-mono">{p.target_allocation}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Detail Panel */}
        {selectedProfile && detailData && (
          <div className="border border-white/10 bg-[#090B0F]" data-testid="profile-detail-panel">
            <div className={`px-5 py-3 border-b ${PROFILE_COLORS[selectedProfile].border} ${PROFILE_COLORS[selectedProfile].bg}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {React.createElement(PROFILE_ICONS[selectedProfile], { className: `w-4 h-4 ${PROFILE_COLORS[selectedProfile].text}` })}
                  <h2 className="text-sm font-bold text-white">{detailData.name} Profile — Agent Detail</h2>
                </div>
                <span className="text-[10px] text-slate-500 font-mono">{detailData.agents?.length} agents active</span>
              </div>
            </div>

            <div className="p-4 space-y-2">
              {detailData.agents?.length === 0 ? (
                <div className="text-center py-8">
                  <Activity className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">{t('profiles.noAgents')}</p>
                </div>
              ) : (
                detailData.agents.map(agent => (
                  <AgentCard key={agent.id} agent={agent} colors={PROFILE_COLORS[selectedProfile]} />
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
